import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Plus,
  Pencil,
  Package,
  AlertTriangle,
  ArrowUpDown,
  Search,
  QrCode,
  Tag,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { EtiquetasModal, type ProdutoSelecionado } from './components/EtiquetasModal';
import { ExcluirProdutoModal } from './components/ExcluirProdutoModal';
import { CategorizarLoteModal } from './components/CategorizarLoteModal';
import { createProdutoInput, ajustarEstoqueInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreateProdutoInput = z.infer<typeof createProdutoInput>;
type AjustarEstoqueInput = z.infer<typeof ajustarEstoqueInput>;

export function EstoquePage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAjusteModalOpen, setIsAjusteModalOpen] = useState(false);
  const [isEtiquetasModalOpen, setIsEtiquetasModalOpen] = useState(false);
  const [isCategorizarModalOpen, setIsCategorizarModalOpen] = useState(false);
  const [selecionados, setSelecionados] = useState<Record<string, ProdutoSelecionado>>({});
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [excluindo, setExcluindo] = useState<{ id: string; nome: string; codigo: string } | null>(
    null
  );
  const [aviso, setAviso] = useState<string | null>(null);
  const [editingProduto, setEditingProduto] = useState<{
    id: string;
    nome: string;
    codigo: string;
    categoria: string | null;
    descricao: string | null;
    preco: string;
    quantidadeEstoque: number;
    ativo: boolean;
  } | null>(null);
  const [ajustandoProduto, setAjustandoProduto] = useState<{
    id: string;
    nome: string;
    quantidadeEstoque: number;
  } | null>(null);

  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.produtos.list.useQuery({
    page,
    limit: 10,
    search: search || undefined,
    categoria: categoriaFiltro || undefined,
    // Sem o filtro, a lista mostra só as ativas: peça desativada sai da frente
    // mas continua alcançável, com a opção de reativar.
    ativo: mostrarInativas ? undefined : true,
  });

  const { data: categorias } = trpc.produtos.listCategorias.useQuery();

  const createMutation = trpc.produtos.create.useMutation({
    onSuccess: () => {
      utils.produtos.list.invalidate();
      utils.produtos.listCategorias.invalidate();
      setIsModalOpen(false);
      resetProduto();
    },
  });

  const updateMutation = trpc.produtos.update.useMutation({
    onSuccess: () => {
      utils.produtos.list.invalidate();
      utils.produtos.listCategorias.invalidate();
      setIsModalOpen(false);
      setEditingProduto(null);
      resetProduto();
    },
  });

  const reativarMutation = trpc.produtos.reativar.useMutation({
    onSuccess: (resultado) => {
      utils.produtos.list.invalidate();
      setAviso(resultado.message ?? 'Peça reativada');
    },
  });

  const ajustarEstoqueMutation = trpc.produtos.ajustarEstoque.useMutation({
    onSuccess: () => {
      utils.produtos.list.invalidate();
      setIsAjusteModalOpen(false);
      setAjustandoProduto(null);
      resetAjuste();
    },
  });

  const {
    register: registerProduto,
    handleSubmit: handleSubmitProduto,
    reset: resetProduto,
    setValue: setValueProduto,
    formState: { errors: errorsProduto },
  } = useForm<CreateProdutoInput>({
    resolver: zodResolver(createProdutoInput),
    defaultValues: {
      quantidadeEstoque: 0,
    },
  });

  const {
    register: registerAjuste,
    handleSubmit: handleSubmitAjuste,
    reset: resetAjuste,
    formState: { errors: errorsAjuste },
  } = useForm<AjustarEstoqueInput>({
    resolver: zodResolver(ajustarEstoqueInput),
  });

  const onSubmitProduto = (data: CreateProdutoInput) => {
    // Campo em branco significa "não mexe" (ou "gera automaticamente" no
    // cadastro), não "grava string vazia" — que o schema recusaria.
    const payload = {
      ...data,
      // O código nunca vem do formulário: no cadastro a sequence gera, na
      // edição ele fica intocado.
      codigo: undefined,
      categoria: data.categoria?.trim() || undefined,
    };

    if (editingProduto) {
      updateMutation.mutate({ id: editingProduto.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const onSubmitAjuste = (data: AjustarEstoqueInput) => {
    if (!ajustandoProduto) return;
    ajustarEstoqueMutation.mutate({
      ...data,
      produtoId: ajustandoProduto.id,
    });
  };

  const handleEdit = (produto: {
    id: string;
    nome: string;
    codigo: string;
    categoria: string | null;
    descricao: string | null;
    preco: string;
    quantidadeEstoque: number;
    ativo: boolean;
  }) => {
    setEditingProduto(produto);
    setValueProduto('nome', produto.nome);
    setValueProduto('categoria', produto.categoria || '');
    setValueProduto('descricao', produto.descricao || '');
    setValueProduto('preco', produto.preco);
    setValueProduto('quantidadeEstoque', produto.quantidadeEstoque);
    setIsModalOpen(true);
  };

  const handleAjustarEstoque = (produto: {
    id: string;
    nome: string;
    quantidadeEstoque: number;
  }) => {
    setAjustandoProduto(produto);
    setIsAjusteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduto(null);
    resetProduto();
  };

  const handleCloseAjusteModal = () => {
    setIsAjusteModalOpen(false);
    setAjustandoProduto(null);
    resetAjuste();
  };

  const alternarSelecao = (produto: ProdutoSelecionado) => {
    setSelecionados((atual) => {
      if (atual[produto.id]) {
        const { [produto.id]: _removido, ...resto } = atual;
        return resto;
      }
      return { ...atual, [produto.id]: produto };
    });
  };

  /**
   * O snapshot é tirado quando a caixa é marcada, e a seleção atravessa
   * páginas — então nem sempre dá para reler tudo do servidor. Sobrepomos os
   * dados frescos da página atual sobre o snapshot, para uma peça que teve o
   * preço editado depois de selecionada não sair com o preço antigo na
   * etiqueta.
   */
  const produtosSelecionados = Object.values(selecionados).map((selecionado) => {
    const atual = data?.items.find((p) => p.id === selecionado.id);
    return atual
      ? {
          id: atual.id,
          codigo: atual.codigo,
          nome: atual.nome,
          preco: atual.preco,
          quantidadeEstoque: atual.quantidadeEstoque,
          categoria: atual.categoria,
        }
      : selecionado;
  });

  // A seleção atravessa páginas de propósito: dá para juntar peças de páginas
  // diferentes numa folha só de etiquetas.
  const paginaToda = data?.items ?? [];
  const paginaTodaSelecionada =
    paginaToda.length > 0 && paginaToda.every((p) => Boolean(selecionados[p.id]));

  const alternarPaginaToda = () => {
    setSelecionados((atual) => {
      if (paginaTodaSelecionada) {
        const resto = { ...atual };
        for (const p of paginaToda) delete resto[p.id];
        return resto;
      }
      const proximo = { ...atual };
      for (const p of paginaToda) {
        proximo[p.id] = {
          id: p.id,
          codigo: p.codigo,
          nome: p.nome,
          preco: p.preco,
          quantidadeEstoque: p.quantidadeEstoque,
          categoria: p.categoria,
        };
      }
      return proximo;
    });
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Estoque</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsCategorizarModalOpen(true)}
            disabled={!canEdit || produtosSelecionados.length === 0}
            title={
              produtosSelecionados.length === 0
                ? 'Selecione produtos na lista para categorizar'
                : undefined
            }
          >
            <Tag className="mr-2 h-4 w-4" />
            Categorizar
            {produtosSelecionados.length > 0 && ` (${produtosSelecionados.length})`}
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsEtiquetasModalOpen(true)}
            disabled={!canEdit || produtosSelecionados.length === 0}
            title={
              produtosSelecionados.length === 0
                ? 'Selecione produtos na lista para imprimir etiquetas'
                : undefined
            }
          >
            <QrCode className="mr-2 h-4 w-4" />
            Imprimir etiquetas
            {produtosSelecionados.length > 0 && ` (${produtosSelecionados.length})`}
          </Button>
          <Button onClick={() => setIsModalOpen(true)} disabled={!canEdit}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Produto
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar produto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <select
              value={categoriaFiltro}
              onChange={(e) => {
                setCategoriaFiltro(e.target.value);
                setPage(1);
              }}
              aria-label="Filtrar por categoria"
              className="focus:border-primary-500 focus:ring-primary-500 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1"
            >
              <option value="">Todas as categorias</option>
              {categorias?.map((categoria) => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={mostrarInativas}
                onChange={(e) => {
                  setMostrarInativas(e.target.checked);
                  setPage(1);
                }}
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              Mostrar inativas
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        aria-label="Selecionar todos os produtos da página"
                        checked={paginaTodaSelecionada}
                        onChange={alternarPaginaToda}
                        className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                      />
                    </TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Preço</TableHead>
                    <TableHead>Estoque</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((produto) => (
                    <TableRow key={produto.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${produto.nome}`}
                          checked={Boolean(selecionados[produto.id])}
                          onChange={() =>
                            alternarSelecao({
                              id: produto.id,
                              codigo: produto.codigo,
                              nome: produto.nome,
                              preco: produto.preco,
                              quantidadeEstoque: produto.quantidadeEstoque,
                              categoria: produto.categoria,
                            })
                          }
                          className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                            <Package className="h-5 w-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium">{produto.nome}</p>
                            {produto.descricao && (
                              <p className="text-sm text-gray-500">{produto.descricao}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700">
                          {produto.codigo}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {produto.categoria || <span className="text-gray-400">—</span>}
                      </TableCell>
                      <TableCell>{formatCurrency(produto.preco)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-medium ${
                              produto.quantidadeEstoque < 10 ? 'text-red-600' : 'text-gray-900'
                            }`}
                          >
                            {produto.quantidadeEstoque}
                          </span>
                          {produto.quantidadeEstoque < 10 && (
                            <AlertTriangle
                              className="h-4 w-4 text-amber-500"
                              aria-label="Estoque baixo"
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            produto.ativo
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {produto.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(produto)}
                            disabled={!canEdit}
                            className={!canEdit ? 'cursor-not-allowed opacity-50' : ''}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAjustarEstoque(produto)}
                            disabled={!canEdit}
                            className={!canEdit ? 'cursor-not-allowed opacity-50' : ''}
                            title="Ajustar estoque"
                          >
                            <ArrowUpDown className="h-4 w-4" />
                          </Button>
                          {produto.ativo ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setExcluindo({
                                  id: produto.id,
                                  nome: produto.nome,
                                  codigo: produto.codigo,
                                })
                              }
                              disabled={!canEdit}
                              className={!canEdit ? 'cursor-not-allowed opacity-50' : ''}
                              title="Excluir peça"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => reativarMutation.mutate({ id: produto.id })}
                              disabled={!canEdit || reativarMutation.isPending}
                              className={!canEdit ? 'cursor-not-allowed opacity-50' : ''}
                              title="Reativar peça"
                            >
                              <RotateCcw className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data && <Pagination meta={data.meta} onPageChange={setPage} itemLabel="produtos" />}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar Produto */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProduto ? 'Editar Produto' : 'Novo Produto'}
      >
        <form onSubmit={handleSubmitProduto(onSubmitProduto)} className="space-y-4">
          <Input label="Nome" {...registerProduto('nome')} error={errorsProduto.nome?.message} />
          {/*
            O código não é editável: ele é gerado no cadastro e vai impresso na
            etiqueta. Trocá-lo depois quebraria em silêncio toda etiqueta já
            colada nas peças — elas parariam de ser reconhecidas no PDV e
            ninguém descobriria até o caixa tentar escanear.
          */}
          {editingProduto && (
            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Código da etiqueta
              </span>
              <div className="flex items-center gap-2">
                <span className="rounded bg-gray-100 px-3 py-2 font-mono text-sm text-gray-700">
                  {editingProduto.codigo}
                </span>
                <span className="text-xs text-gray-500">gerado automaticamente</span>
              </div>
            </div>
          )}
          <div>
            <Input
              label="Categoria"
              list="categorias-cadastradas"
              placeholder="Ex.: Roupas"
              {...registerProduto('categoria')}
              error={errorsProduto.categoria?.message}
            />
            <datalist id="categorias-cadastradas">
              {categorias?.map((categoria) => (
                <option key={categoria} value={categoria} />
              ))}
            </datalist>
          </div>
          <Input
            label="Descrição"
            {...registerProduto('descricao')}
            error={errorsProduto.descricao?.message}
          />
          <Input
            label="Preço"
            type="number"
            step="0.01"
            {...registerProduto('preco')}
            error={errorsProduto.preco?.message}
          />
          <Input
            label="Quantidade em Estoque"
            type="number"
            {...registerProduto('quantidadeEstoque', { valueAsNumber: true })}
            error={errorsProduto.quantidadeEstoque?.message}
          />
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingProduto ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      <ExcluirProdutoModal
        produto={excluindo}
        onClose={() => setExcluindo(null)}
        onExcluido={setAviso}
      />

      {aviso && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-lg"
        >
          {aviso}
          <button
            type="button"
            onClick={() => setAviso(null)}
            className="text-gray-400 hover:text-white"
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}

      <CategorizarLoteModal
        isOpen={isCategorizarModalOpen}
        onClose={() => setIsCategorizarModalOpen(false)}
        produtos={produtosSelecionados}
        categorias={categorias ?? []}
        onAplicado={setAviso}
      />

      <EtiquetasModal
        isOpen={isEtiquetasModalOpen}
        onClose={() => setIsEtiquetasModalOpen(false)}
        produtos={produtosSelecionados}
      />

      {/* Modal Ajustar Estoque */}
      <Modal isOpen={isAjusteModalOpen} onClose={handleCloseAjusteModal} title="Ajustar Estoque">
        {ajustandoProduto && (
          <form onSubmit={handleSubmitAjuste(onSubmitAjuste)} className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-4">
              <p className="font-medium">{ajustandoProduto.nome}</p>
              <p className="text-sm text-gray-500">
                Estoque atual: {ajustandoProduto.quantidadeEstoque}
              </p>
            </div>
            <input type="hidden" {...registerAjuste('produtoId')} value={ajustandoProduto.id} />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Tipo de Movimentação
              </label>
              <select
                {...registerAjuste('tipo')}
                className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1"
              >
                <option value="ENTRADA">Entrada</option>
                <option value="SAIDA">Saída</option>
              </select>
            </div>
            <Input
              label="Quantidade"
              type="number"
              min="1"
              {...registerAjuste('quantidade', { valueAsNumber: true })}
              error={errorsAjuste.quantidade?.message}
            />
            <Input
              label="Motivo"
              {...registerAjuste('motivo')}
              error={errorsAjuste.motivo?.message}
            />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseAjusteModal}>
                Cancelar
              </Button>
              <Button type="submit" isLoading={ajustarEstoqueMutation.isPending}>
                Confirmar
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export default EstoquePage;
